import React, { useState, useCallback } from "react";
import { usePolling } from "../hooks/use-polling";
import { agentTasks } from "../lib/api";
import { Loader2, RefreshCw } from "lucide-react";

interface RegenerationQueueMonitorProps {
  siteId: string;
}

interface RegenProgress {
  total: number;
  imported: number;
  queued: number;
  regenerating: number;
  regenerated: number;
  failed: number;
  published: number;
}

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  imported: { label: "Imported", color: "bg-zinc-500/10 text-zinc-400" },
  queued: { label: "Queued", color: "bg-amber-500/10 text-amber-400" },
  regenerating: { label: "Regenerating", color: "bg-blue-500/10 text-blue-400" },
  regenerated: { label: "Regenerated", color: "bg-green-500/10 text-green-400" },
  failed: { label: "Failed", color: "bg-red-500/10 text-red-400" },
  published: { label: "Published", color: "bg-emerald-500/10 text-emerald-400" },
};

const RegenerationQueueMonitor: React.FC<RegenerationQueueMonitorProps> = (props) => {
  const { siteId } = props;
  const [progress, setProgress] = useState<RegenProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const poll = useCallback(async () => {
    try {
      const res = await agentTasks.getRegenerationProgress(siteId);
      setProgress(res);
      setLoading(false);
      setError(null);
    } catch (err: unknown) {
      if (loading) setLoading(false);
      setError(err instanceof Error ? err.message : "Failed to fetch progress");
    }
  }, [siteId, loading]);

  usePolling(poll, 10_000);

  // Initial fetch
  if (loading && !progress) {
    void poll();
  }

  if (loading && !progress) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
          <span className="text-sm text-zinc-400">Loading regeneration progress...</span>
        </div>
      </div>
    );
  }

  if (error && !progress) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (!progress) return null;

  const completedCount = progress.regenerated + progress.published;
  const progressPct = progress.total > 0 ? (completedCount / progress.total) * 100 : 0;

  const rows: { key: keyof RegenProgress; count: number }[] = [
    { key: "imported", count: progress.imported },
    { key: "queued", count: progress.queued },
    { key: "regenerating", count: progress.regenerating },
    { key: "regenerated", count: progress.regenerated },
    { key: "published", count: progress.published },
    { key: "failed", count: progress.failed },
  ];

  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-100">Regeneration Queue</h3>
        </div>
        <span className="text-xs text-zinc-500">
          {progress.total} total pages
        </span>
      </div>

      {/* Progress bar with label */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-zinc-400">
            {completedCount}/{progress.total} pages regenerated
          </span>
          <span className="text-xs text-zinc-500">{Math.round(progressPct)}%</span>
        </div>
        <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Breakdown table */}
      <div className="space-y-1.5">
        {rows.map(({ key, count }) => {
          const badge = STATUS_BADGES[key];
          if (!badge) return null;
          return (
            <div key={key} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-700/40 transition-colors">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.color}`}>
                {badge.label}
              </span>
              <span className="text-sm text-zinc-300 tabular-nums">{count}</span>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-xs text-red-400 mt-2">{error}</p>
      )}
    </div>
  );
};

export default RegenerationQueueMonitor;
